import React, { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import Cookie from 'js-cookie';
import {
  SplitFactoryProvider,
  useSplitClient,
} from '@splitsoftware/splitio-react';
import { ACCOUNT_URI_COOKIE_NAME, ENV, HARNESS_SDK_KEY } from '../constants';
import { generateUUID } from '../lib/flagr';
import { setBrowserHarnessFactory } from '../api/harness/browserHarnessFlag';

type PortalReport = {
  accountId?: string;
  account?: {
    id?: string;
    uriName?: string;
  };
};

export const getSessionHarnessAttributes = ({
  accountId,
  accountUri,
  env,
  locationHost,
}: {
  accountId?: string | null;
  accountUri?: string | null;
  env?: string | null;
  locationHost?: string | null;
}): SplitIO.Attributes => ({
  accountId: accountId || 'empty-account-id',
  accountUri: accountUri || 'empty-account-uri',
  env: env || 'empty-env',
  locationHost: locationHost || 'empty-location-host',
});

const ANONYMOUS_KEY_STORAGE_KEY = 'harness_anonymous_key';

function getAnonymousKey(): string {
  try {
    const stored = localStorage.getItem(ANONYMOUS_KEY_STORAGE_KEY);
    if (stored) return stored;

    const key = generateUUID();
    localStorage.setItem(ANONYMOUS_KEY_STORAGE_KEY, key);
    return key;
  } catch {
    return generateUUID();
  }
}

type HarnessProviderWrapperProps = {
  children: React.ReactNode;
};

const HarnessClientBridge: React.FC<HarnessProviderWrapperProps> = ({
  children,
}) => {
  const { factory } = useSplitClient();

  useEffect(() => {
    setBrowserHarnessFactory(factory ?? null);
    return () => setBrowserHarnessFactory(null);
  }, [factory]);

  return <>{children}</>;
};

const HarnessProviderWrapper: React.FC<HarnessProviderWrapperProps> = ({
  children,
}) => {
  const report = useSelector(
    (state: { portal?: { reports?: PortalReport[] } }) =>
      state?.portal?.reports?.[0],
  );
  const attributes = getSessionHarnessAttributes({
    accountId: report?.accountId || report?.account?.id,
    accountUri: report?.account?.uriName || Cookie.get(ACCOUNT_URI_COOKIE_NAME),
    env: ENV,
    locationHost: window.location.host,
  });

  const sdkConfig: SplitIO.IBrowserSettings = useMemo(() => {
    // 'localhost' is a documented Split.io SDK mode that returns 'control'
    // for all flags without making network requests (local dev, tests)
    if (!HARNESS_SDK_KEY) {
      return {
        core: {
          authorizationKey: 'localhost',
          key: 'anonymous',
        },
      };
    }

    return {
      core: {
        authorizationKey: HARNESS_SDK_KEY,
        key: getAnonymousKey(),
      },
      storage: { type: 'LOCALSTORAGE' as const },
      startup: { readyTimeout: 10 },
      scheduler: {
        featuresRefreshRate: 30,
        segmentsRefreshRate: 60,
        impressionsRefreshRate: 60,
      },
    };
  }, []);

  return (
    <SplitFactoryProvider config={sdkConfig} attributes={attributes}>
      <HarnessClientBridge>{children}</HarnessClientBridge>
    </SplitFactoryProvider>
  );
};

export default HarnessProviderWrapper;
